import type { VinFormatType, VinNumber } from '@navio/nsdk';
import { NoddiDialog, NoddiInfoField } from 'noddi-ui-web';

export type VinNumberDetailsTranslations = {
  title: string;
  description: string;
  wmi: string;
  wmiTooltip: string;
  vds: string;
  vdsTooltip: string;
  checkDigit: string;
  checkDigitTooltip: string;
  modelYear: string;
  modelYearTooltip: string;
  assemblyPlant: string;
  assemblyPlantTooltip: string;
  productionNumber: string;
  productionNumberTooltip: string;
  formatType: string;
  formatTypeTooltip: string;
  year: string;
  yearTooltip: string;
  euVin: string;
  usVin: string;
  legacyChassis: string;
};

const defaultTexts: VinNumberDetailsTranslations = {
  title: 'VIN number details',
  description: 'The VIN is a 17-character identifier. Each field below is one section of that number.',
  wmi: 'World Manufacturer Identifier (WMI)',
  wmiTooltip:
    'Characters 1-3 of the VIN. Identifies the manufacturer and the country or region it is assigned to. Assigned by a national VIN authority under ISO 3779.',
  vds: 'Vehicle Descriptor Section (VDS)',
  vdsTooltip:
    'Characters 4-8 of the VIN. Manufacturer-specific codes that describe the vehicle, typically model, body type, and engine.',
  checkDigit: 'Check digit',
  checkDigitTooltip:
    'Character 9 of the VIN. Calculated from the other characters to catch typing errors. Often empty on European VINs, which do not always include a check digit.',
  modelYear: 'Model year',
  modelYearTooltip:
    'Character 10 of the VIN. A single letter or number that encodes the model year on a repeating 30-year cycle. The decoded calendar year is shown in Year.',
  assemblyPlant: 'Assembly plant',
  assemblyPlantTooltip:
    'Character 11 of the VIN. A manufacturer-assigned code for the factory where this vehicle was assembled.',
  productionNumber: 'Production number',
  productionNumberTooltip:
    'Characters 12-17 of the VIN. Sequential serial number assigned by the manufacturer to uniquely identify this vehicle.',
  formatType: 'Format type',
  formatTypeTooltip:
    'Which identifier scheme this number follows. EU VIN and US VIN are 17-character ISO 3779 numbers (US requires a check digit). Legacy chassis is an older identifier that is not a full VIN.',
  year: 'Year',
  yearTooltip:
    'The decoded model year of the vehicle, derived from the model-year character. The same character repeats every 30 years, so this is the calendar year that best matches this car.',
  euVin: 'EU VIN',
  usVin: 'US VIN',
  legacyChassis: 'Legacy chassis'
};

const formatVinFormatType = (formatType: VinFormatType | undefined, texts: VinNumberDetailsTranslations): string => {
  if (!formatType) {
    return '-';
  }
  const labels: Record<VinFormatType, string> = {
    eu_vin: texts.euVin,
    us_vin: texts.usVin,
    legacy_chassis: texts.legacyChassis
  };
  return labels[formatType];
};

interface Props {
  vinNumberModel: VinNumber;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translations?: VinNumberDetailsTranslations;
}

export const VinNumberDetailsDialog = ({ vinNumberModel, open, onOpenChange, translations }: Props) => {
  const texts = translations ?? defaultTexts;

  return (
    <NoddiDialog open={open} onClose={() => onOpenChange(false)} title={texts.title} description={texts.description}>
      <div className='flex flex-col gap-3'>
        <NoddiInfoField label={texts.wmi} value={vinNumberModel.wmi} tooltip={texts.wmiTooltip} variant='column' />
        <NoddiInfoField
          label={texts.vds}
          value={vinNumberModel.vdsAttributes}
          tooltip={texts.vdsTooltip}
          variant='column'
        />
        <NoddiInfoField
          label={texts.checkDigit}
          value={vinNumberModel.checkDigit ?? '-'}
          tooltip={texts.checkDigitTooltip}
          variant='column'
        />
        <NoddiInfoField
          label={texts.modelYear}
          value={vinNumberModel.modelYear}
          tooltip={texts.modelYearTooltip}
          variant='column'
        />
        <NoddiInfoField
          label={texts.assemblyPlant}
          value={vinNumberModel.assemblyPlant}
          tooltip={texts.assemblyPlantTooltip}
          variant='column'
        />
        <NoddiInfoField
          label={texts.productionNumber}
          value={vinNumberModel.productionNumber}
          tooltip={texts.productionNumberTooltip}
          variant='column'
        />
        <NoddiInfoField
          label={texts.formatType}
          value={formatVinFormatType(vinNumberModel.formatType, texts)}
          tooltip={texts.formatTypeTooltip}
          variant='column'
        />
        {vinNumberModel.year != null && (
          <NoddiInfoField
            label={texts.year}
            value={String(vinNumberModel.year)}
            tooltip={texts.yearTooltip}
            variant='column'
          />
        )}
      </div>
    </NoddiDialog>
  );
};
